import { useEffect, useState } from "react";
import { useGlobalDropGuard } from "./FileDropZone";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { TYPE_CATALOGS_ANY_OF } from "./TypeCatalogSwitch";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { ATTRIBUTES_SCREEN, codebookWrite } from "../lib/codebookFunctions";
import { FRONTEND_VERSION, buildsDiffer, formatVersion, useBackendVersion } from "../lib/appVersion";
import { resolveTheme } from "../lib/theme";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./GlobalSearch";
import { TooltipLayer } from "./Tooltip";
import Ic from "./icons.jsx";
import { InstallAppNavItem } from "../pwa/InstallAppNavItem";
import { DevUserSwitcher } from "./DevUserSwitcher";
import type { ComponentType, ReactNode } from "react";

/** Početna shows only structure and asset data — without either it leaves the menu and `/` redirects (DashboardPage). */
const DASHBOARD_ANY_OF = ["structure:view", "assets:view"];

/** The part search draws on the type catalogue and the asset list: both views (audit 2026-09-23 §B2). */
const PART_SEARCH = ["assets:view", "types:view"];

/** The topbar search returns each group only to callers with that module's view; any one of them opens the box. */
const SEARCH_ANY_OF = ["assets:view", "structure:view", "types:view"];

/**
 * Shell width bands (docs/blueprint/03-dizajn-sistem.md §breakpoints): a phone gets the off-canvas
 * drawer, a tablet the 64px icon rail (the full sidebar opens OVER the content, never beside it —
 * 240px beside the content left a portrait iPad with a 160px card, Strahinja 2026-09-23), a desktop
 * the sidebar the user may collapse.
 */
type ShellMode = "phone" | "tablet" | "desktop";
const PHONE_MQ = "(max-width: 820px)";
const TABLET_MQ = "(min-width: 821px) and (max-width: 1200px)";

function currentShellMode(): ShellMode {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "desktop";
  if (window.matchMedia(PHONE_MQ).matches) return "phone";
  if (window.matchMedia(TABLET_MQ).matches) return "tablet";
  return "desktop";
}

function useShellMode(): ShellMode {
  const [mode, setMode] = useState<ShellMode>(currentShellMode);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const queries = [window.matchMedia(PHONE_MQ), window.matchMedia(TABLET_MQ)];
    const onChange = () => setMode(currentShellMode());
    queries.forEach((q) => q.addEventListener("change", onChange));
    return () => queries.forEach((q) => q.removeEventListener("change", onChange));
  }, []);
  return mode;
}

type NavEntry = {
  to: string;
  label: string;
  icon: ComponentType<Record<string, unknown>>;
  permission?: string;
  /** Force-hide regardless of permission. */
  hidden?: boolean;
  /** Show if the caller holds at least one of these functions. */
  anyOf?: string[];
  /** Show only if the caller holds every one of these functions. */
  allOf?: string[];
  /** Require exact match for active state. Use for a parent route whose
   *  prefix would otherwise highlight every child route alongside the leaf. */
  end?: boolean;
  /** Renders as a non-navigable "coming soon" row instead of a link — the
   *  route doesn't exist yet. */
  disabled?: boolean;
  /** Optional numeric badge shown next to the label. */
  badge?: number;
};

/** Code lists (location / position kinds) are administered by whoever may write them (sprint 7 §5). */

type NavGroup = {
  id: string;
  title: string;
  items: NavEntry[];
};

export function AppShell() {
  useGlobalDropGuard();
  const { t } = useTranslation();
  const { state, logout, hasPermission } = useAuth();
  // Which build is in front of the user (2026-10-09): the server's once it answers, the bundle's until then.
  const backendVersion = useBackendVersion();
  const navigate = useNavigate();

  const mode = useShellMode();

  // Desktop: the sidebar may be collapsed to the icon rail. Stored only when the user toggles it —
  // writing it on every mount turned "never chose" into a remembered "expanded".
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bkm.navCollapsed") === "1"; } catch { return false; }
  });

  // Phone drawer / tablet overlay — closes on every route change (a nav item was tapped), when the
  // width band changes (device rotated) and on Escape.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setNavOpen(false); }, [location.pathname, mode]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  function toggleNav() {
    if (mode !== "desktop") {
      setNavOpen((o) => !o);
      return;
    }
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("bkm.navCollapsed", next ? "1" : "0"); } catch { /* ignore */ }
  }

  if (state.status !== "authenticated") return null;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const groups: NavGroup[] = [
    {
      id: "overview",
      title: t("nav.group.overview", "Pregled"),
      items: [{ to: "/", label: t("nav.home", "Početna"), icon: Ic.dashboard, end: true, anyOf: DASHBOARD_ANY_OF }],
    },
    {
      // Phase 1 — equipment register (sprint 3 contract §7 order; sprint 4 adds Šabloni).
      id: "oprema",
      title: t("nav.group.equipment", "Oprema"),
      items: [
        { to: "/struktura", label: t("nav.structure", "Struktura"), icon: Ic.tree, permission: "structure:view" },
        { to: "/asseti", label: t("nav.assets", "Asseti"), icon: Ic.list, permission: "assets:view" },
        // Sprint 5l: the quantity-tracked catalogue (articles), next to the assets.
        { to: "/artikli", label: t("nav.articles", "Artikli"), icon: Ic.shelves, permission: "articles:view" },
        { to: "/pretraga", label: t("nav.partSearch", "Pretraga dela"), icon: Ic.search, allOf: PART_SEARCH },
        // One item for both type catalogues (2026-10-12): article types are the second view of /tipovi.
        { to: "/tipovi", label: t("nav.partTypes", "Tipovi delova"), icon: Ic.box, anyOf: TYPE_CATALOGS_ANY_OF },
        { to: "/layout", label: t("nav.layouts", "Layout"), icon: Ic.layout, permission: "layouts:view" },
        { to: "/sabloni", label: t("nav.templates", "Šabloni"), icon: Ic.template, permission: "templates:view" },
        { to: "/lokacije", label: t("nav.locations", "Lokacije"), icon: Ic.pin, permission: "locations:view" },
      ],
    },
    {
      id: "admin",
      title: t("nav.group.admin", "Administracija"),
      items: [
        { to: "/admin/korisnici", label: t("nav.users", "Korisnici"), icon: Ic.user, permission: "users:view" },
        { to: "/admin/uloge", label: t("nav.roles", "Uloge"), icon: Ic.grid, permission: "roles:view" },
        { to: "/admin/audit", label: t("nav.adminAudit", "Audit log"), icon: Ic.book, permission: "audit:view" },
        { to: "/admin/vrste-lokacija", label: t("nav.locationKinds", "Vrste lokacija"), icon: Ic.tag, anyOf: codebookWrite("location-kinds") },
        { to: "/admin/vrste-pozicija", label: t("nav.positionKinds", "Vrste pozicija"), icon: Ic.layers, anyOf: codebookWrite("position-kinds") },
        { to: "/admin/atributi", label: t("nav.attributes", "Atributi i jedinice"), icon: Ic.catalog, anyOf: ATTRIBUTES_SCREEN },
        { to: "/admin/proizvodjaci", label: t("nav.manufacturers", "Proizvođači"), icon: Ic.factory, anyOf: codebookWrite("manufacturers") },
        { to: "/admin/dobavljaci", label: t("nav.suppliers", "Dobavljači"), icon: Ic.truck, anyOf: codebookWrite("suppliers") },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (it.hidden) return false;
        if (it.allOf) return it.allOf.every((p) => hasPermission(p));
        if (it.anyOf) return it.anyOf.some((p) => hasPermission(p));
        if (it.permission) return hasPermission(it.permission);
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  const initials = `${state.user.firstName?.[0] ?? ""}${state.user.lastName?.[0] ?? ""}`.toUpperCase() || "—";
  const stale = buildsDiffer(FRONTEND_VERSION, backendVersion);
  // Icon rail: always on a tablet, on a desktop when collapsed. The tablet overlay shows the labels
  // again over the content, so the grid keeps the 64px column (no jump) and tooltips switch off.
  const rail = mode === "tablet" || (mode === "desktop" && collapsed);
  const overlayOpen = mode === "tablet" && navOpen;
  const drawerOpen = mode === "phone" && navOpen;
  const tips = rail && !overlayOpen;

  return (
    <div className={`app${rail ? " nav-collapsed" : ""}${overlayOpen ? " nav-overlay-open" : ""}${drawerOpen ? " mobile-nav-open" : ""}`}>
      <TooltipLayer />
      <div className="mobile-nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <aside className="sidebar">
        <Link to="/" className="brand" style={{ textDecoration: "none" }}>
          <span className="brand-mark" style={{ overflow: "hidden", padding: 3, background: "var(--surface)" }}>
            {/* Just the leaf mark (favicon), not the full wordmark logo: the "Bankom"
                text sits right next to it, so the wordmark would be redundant and
                shrink the mark to an illegible size. */}
            <img src="/favicon.svg" alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </span>
          <span className="brand-text">
            <b>Bankom</b>
            <span>{t("app.tagline", "CMMS · Održavanje")}</span>
          </span>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          onClick={toggleNav}
          aria-label={t("nav.toggleMenu", "Uključi/isključi meni")}
          aria-expanded={mode === "desktop" ? !collapsed : overlayOpen}
          title={t("nav.toggleMenu", "Uključi/isključi meni")}
        >
          <Ic.arrowRight />
        </button>

        <nav className="nav">
          {visibleGroups.map((group) => (
            <div key={group.id}>
              <div className="nav-section">{group.title}</div>
              {group.items.map((it) => (
                it.disabled ? (
                  <DisabledNavItem key={it.to} label={it.label} icon={<it.icon />} />
                ) : (
                  <NavItem key={it.to} to={it.to} label={it.label} icon={<it.icon />} end={it.end} badge={it.badge} tip={tips} />
                )
              ))}
            </div>
          ))}
          <InstallAppNavItem tip={tips} />
        </nav>

        <div className="sidebar-foot">
          {/* The avatar itself opens the profile — in the collapsed rail it is the only thing left. */}
          <Link to="/profil" className="user-avatar-link" title={t("nav.profile", "Profil")} aria-label={t("nav.profile", "Profil")}>
            {state.user.avatarUrl ? (
              <img
                src={state.user.avatarUrl}
                alt=""
                className="user-avatar"
                style={{ padding: 0, objectFit: "cover", border: "1px solid var(--border)" }}
              />
            ) : (
              <span className="user-avatar">{initials}</span>
            )}
          </Link>
          <div className="user-meta">
            <b>{state.user.firstName} {state.user.lastName}</b>
            <span>{t("app.site", "Bankom · Obrenovac")}</span>
            <Link to="/profil" className={`app-version${stale ? " is-stale" : ""}`} title={t("app.version", "Verzija aplikacije")}>
              {formatVersion(backendVersion ?? FRONTEND_VERSION)}
              {stale && ` · ${t("app.versionStale", "osveži")}`}
            </Link>
          </div>
          <Link to="/profil" className="icon-btn sidebar-foot-profile" title={t("nav.profile", "Profil")}>
            <Ic.user />
          </Link>
        </div>
      </aside>

      <header className="topbar">
        <button
          type="button"
          className="mobile-nav-btn"
          onClick={() => setNavOpen((o) => !o)}
          aria-label={t("nav.toggleMenu", "Uključi/isključi meni")}
          aria-expanded={drawerOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        {SEARCH_ANY_OF.some((p) => hasPermission(p)) && <GlobalSearch />}
        <div className="topbar-actions" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <DevUserSwitcher />
          <ThemeToggle />
          <NotificationBell />
          <button type="button" className="topbar-logout" onClick={handleLogout} title={t("nav.logout", "Odjava")}>
            {t("nav.logout", "Odjava")}
          </button>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to, label, icon, end, badge, tip,
}: { to: string; label: string; icon: ReactNode; end?: boolean; badge?: number; tip?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-tip={tip ? label : undefined}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      style={{ cursor: "pointer" }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge}`}
          style={{
            background: "var(--brand-primary)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 7px",
            borderRadius: 999,
            minWidth: 18,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

/** A nav row for a module that has no route yet. Shown so the nav structure
 *  is visible ahead of time, without linking anywhere. */
/**
 * One-click light/dark switch in the topbar (next to the bell). It writes the explicit
 * choice to the account (a user on "system" who clicks it wants the opposite of
 * what they currently see); the three-way choice lives on the profile page.
 */
function ThemeToggle() {
  const { state, setTheme } = useAuth();
  const { t } = useTranslation();
  if (state.status !== "authenticated") return null;
  const dark = resolveTheme(state.user.theme) === "dark";
  const label = dark ? t("nav.themeLight", "Svetla tema") : t("nav.themeDark", "Tamna tema");
  return (
    <button
      type="button"
      className="topbar-icon-btn"
      data-tip={label}
      aria-label={label}
      onClick={() => { void setTheme(dark ? "LIGHT" : "DARK").catch(() => {}); }}
    >
      {dark ? <Ic.sun width={18} height={18} /> : <Ic.moon width={18} height={18} />}
    </button>
  );
}

function DisabledNavItem({ label, icon }: { label: string; icon: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div
      className="nav-item"
      aria-disabled="true"
      title={label}
      style={{ cursor: "not-allowed", opacity: 0.55 }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      <span className="badge">{t("nav.comingSoon", "Uskoro")}</span>
    </div>
  );
}
